// Live-audio playback worklet.
//
// This used to be fetched at runtime from a generic S3 bucket
// (convey-embed-scriptlets), which put live audio at the mercy of a bucket
// outside the SDK's own deploy: any missing CORS header made addModule()
// reject, the AudioWorkletNode constructor then threw "audio-processor is not
// registered" and playback stayed silent. The source now ships inside this
// bundle and is registered from a blob URL, so there is no cross-origin fetch
// left to fail and the worklet can never drift from the SDK version using it.

export const AUDIO_PROCESSOR_NAME = "audio-processor";

// Kept as a string because an AudioWorklet runs in its own global scope: it has
// to reach the worklet as standalone source, not as a bundled module.
const AUDIO_PROCESSOR_SOURCE = `
class AudioProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.buffer = new Float32Array();

    // Receive audio data from the main thread, and add it to the buffer
    this.port.onmessage = (event) => {
      const newFetchedData = new Float32Array(this.buffer.length + event.data.audioData.length);
      newFetchedData.set(this.buffer, 0);
      newFetchedData.set(event.data.audioData, this.buffer.length);
      this.buffer = newFetchedData;
    };
  }

  // Take a chunk from the buffer and send it to the output to be played
  process(inputs, outputs, parameters) {
    const output = outputs[0];
    const channel = output[0];
    const bufferLength = this.buffer.length;
    for (let i = 0; i < channel.length; i++) {
      channel[i] = i < bufferLength ? this.buffer[i] : 0;
    }
    this.buffer = this.buffer.slice(channel.length);
    return true;
  }
}

registerProcessor(${JSON.stringify(AUDIO_PROCESSOR_NAME)}, AudioProcessor);
`;

// One blob per page: addModule() is per AudioContext, but the URL backing it can
// be shared by every context the SDK opens.
let moduleUrl: string = null;

const getModuleUrl = (): string => {
  if (!moduleUrl) {
    moduleUrl = URL.createObjectURL(new Blob([AUDIO_PROCESSOR_SOURCE], { type: "text/javascript" }));
  }
  return moduleUrl;
};

export const registerAudioProcessor = async (audioContext: AudioContext): Promise<void> => {
  await audioContext.audioWorklet.addModule(getModuleUrl());
};

export const createAudioProcessorNode = async (audioContext: AudioContext): Promise<AudioWorkletNode> => {
  await registerAudioProcessor(audioContext);
  return new AudioWorkletNode(audioContext, AUDIO_PROCESSOR_NAME);
};
